import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { EmptyState } from "@/components/EmptyState";
import {
  HandCoins, Calendar, ExternalLink, Building2, Plus, Pencil, Trash2, Loader2,
  Filter, Search, X, CheckCircle2, Banknote, Mail, Phone, FileText, Save,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { showError } from "@/lib/errors";

const TARGETS = [
  { v: "all", label: "Tous" },
  { v: "athletes", label: "Athlètes" },
  { v: "clubs", label: "Clubs" },
  { v: "coaches", label: "Coachs" },
  { v: "federations", label: "Fédérations" },
];
const CATEGORIES = [
  { v: "subvention", label: "Subvention" },
  { v: "bourse", label: "Bourse" },
  { v: "sponsoring", label: "Sponsoring" },
  { v: "appel_a_projet", label: "Appel à projet" },
  { v: "concours", label: "Concours" },
  { v: "autre", label: "Autre" },
];
const STATUSES = [
  { v: "draft", label: "Brouillon" },
  { v: "active", label: "Publié" },
  { v: "closed", label: "Clôturé" },
];
const ALL = "__all__";

function fmtDate(iso) {
  if (!iso) return null;
  try { return new Date(iso).toLocaleDateString("fr-FR", { day: "2-digit", month: "long", year: "numeric" }); }
  catch { return iso; }
}

function FundingCard({ item, isAdmin, onEdit, onDelete }) {
  const isExpired = item.deadline && new Date(item.deadline) < new Date();
  return (
    <Card className="p-4 flex flex-col gap-3 aito-card-hover" data-testid={`funding-card-${item.slug}`}>
      <div className="flex items-start gap-3">
        {item.image_url ? (
          <img src={item.image_url} alt={item.title} className="h-20 w-20 object-cover rounded-md shrink-0" onError={(e) => { e.currentTarget.style.display = "none"; }} />
        ) : (
          <div className="h-20 w-20 rounded-md bg-[hsl(var(--aito-gold))]/10 flex items-center justify-center shrink-0">
            <HandCoins className="h-8 w-8 text-[hsl(var(--aito-gold))]" />
          </div>
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 flex-wrap mb-1">
            {(item.categories || []).map((c) => (
              <Badge key={c} variant="secondary" className="text-[10px] capitalize">{c.replace("_", " ")}</Badge>
            ))}
            {item.status === "draft" && <Badge variant="outline" className="text-[10px]">Brouillon</Badge>}
            {item.status === "closed" && <Badge variant="destructive" className="text-[10px]">Clôturé</Badge>}
            {isExpired && item.status === "active" && <Badge variant="destructive" className="text-[10px]">Échéance dépassée</Badge>}
          </div>
          <h3 className="font-display font-semibold text-base leading-tight line-clamp-2">{item.title}</h3>
          <div className="text-xs text-muted-foreground inline-flex items-center gap-1 mt-0.5">
            <Building2 className="h-3 w-3" />{item.organizer}
          </div>
        </div>
      </div>

      {item.description && <p className="text-xs text-muted-foreground line-clamp-3">{item.description}</p>}

      <div className="grid grid-cols-2 gap-2 text-[11px]">
        {item.amount_text && (
          <div className="inline-flex items-center gap-1"><Banknote className="h-3 w-3 text-[hsl(var(--aito-gold))]" />{item.amount_text}</div>
        )}
        {item.deadline && (
          <div className="inline-flex items-center gap-1"><Calendar className="h-3 w-3 text-[hsl(var(--aito-lagoon))]" />Avant le {fmtDate(item.deadline)}</div>
        )}
        {item.target && item.target !== "all" && (
          <div><span className="text-muted-foreground">Pour :</span> <span className="font-medium capitalize">{item.target}</span></div>
        )}
        {(item.disciplines || []).length > 0 && (
          <div className="line-clamp-1"><span className="text-muted-foreground">Disc. :</span> {item.disciplines.join(", ")}</div>
        )}
      </div>

      {item.conditions && (
        <details className="text-xs">
          <summary className="cursor-pointer text-[hsl(var(--aito-lagoon))] hover:underline font-medium">Conditions d'éligibilité</summary>
          <p className="mt-1 whitespace-pre-line text-muted-foreground">{item.conditions}</p>
        </details>
      )}

      <div className="flex items-center gap-2 flex-wrap pt-2 border-t mt-auto">
        {item.url && (
          <Button asChild size="sm" className="bg-[hsl(var(--aito-lagoon))] text-black hover:bg-[hsl(var(--aito-lagoon-2))]" data-testid={`funding-apply-${item.slug}`}>
            <a href={item.url} target="_blank" rel="noreferrer"><ExternalLink className="h-3.5 w-3.5 mr-1" />Postuler / Voir l'aide</a>
          </Button>
        )}
        {item.documents_url && (
          <Button asChild size="sm" variant="outline">
            <a href={item.documents_url} target="_blank" rel="noreferrer"><FileText className="h-3.5 w-3.5 mr-1" />Dossier</a>
          </Button>
        )}
        {item.contact_email && (
          <Button asChild size="sm" variant="ghost">
            <a href={`mailto:${item.contact_email}`}><Mail className="h-3.5 w-3.5 mr-1" />Contact</a>
          </Button>
        )}
        {item.contact_phone && (
          <span className="text-[11px] text-muted-foreground inline-flex items-center gap-1"><Phone className="h-3 w-3" />{item.contact_phone}</span>
        )}
        {isAdmin && (
          <div className="ml-auto flex gap-1">
            <Button size="sm" variant="outline" onClick={() => onEdit(item)} data-testid={`funding-edit-${item.slug}`}>
              <Pencil className="h-3 w-3" />
            </Button>
            <Button size="sm" variant="outline" onClick={() => onDelete(item)} data-testid={`funding-delete-${item.slug}`}>
              <Trash2 className="h-3 w-3" />
            </Button>
          </div>
        )}
      </div>
    </Card>
  );
}

const EMPTY_FORM = {
  title: "", organizer: "", description: "", image_url: "", url: "",
  contact_email: "", contact_phone: "", deadline: "", amount_text: "",
  target: "all", disciplines: [], categories: ["subvention"],
  conditions: "", documents_url: "", organizer_logo_url: "",
  priority_order: 0, status: "draft",
};

function FundingFormDialog({ open, onOpenChange, item, onSaved }) {
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (item) {
      setForm({
        ...EMPTY_FORM, ...item,
        deadline: item.deadline ? item.deadline.split("T")[0] : "",
        disciplines: item.disciplines || [],
        categories: item.categories?.length ? item.categories : ["subvention"],
      });
    } else {
      setForm(EMPTY_FORM);
    }
  }, [open, item]);

  const save = async () => {
    if (!form.title.trim() || !form.organizer.trim()) {
      toast.error("Titre et organisme obligatoires.");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        deadline: form.deadline ? new Date(form.deadline).toISOString() : null,
        priority_order: parseInt(form.priority_order) || 0,
      };
      // Nettoyer chaînes vides → null
      Object.keys(payload).forEach((k) => { if (payload[k] === "") payload[k] = null; });
      let resp;
      if (item?.id) resp = await api.patch(`/funding/${item.id}`, payload);
      else          resp = await api.post("/funding", payload);
      toast.success(item ? "Aide mise à jour." : "Aide créée.");
      onSaved?.(resp.data);
      onOpenChange(false);
    } catch (e) { showError(e, { prefix: "Aide :" }); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="funding-form-dialog">
        <DialogHeader>
          <DialogTitle>{item ? "Modifier l'aide" : "Nouvelle aide aux sportifs"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Titre *</Label>
              <Input data-testid="funding-form-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Subvention COPF 2026" />
            </div>
            <div>
              <Label className="text-xs">Organisme *</Label>
              <Input data-testid="funding-form-organizer" value={form.organizer} onChange={(e) => setForm({ ...form, organizer: e.target.value })} placeholder="COPF, Ministère…" />
            </div>
          </div>
          <div>
            <Label className="text-xs">Description / présentation du projet</Label>
            <Textarea rows={3} data-testid="funding-form-description" value={form.description || ""} onChange={(e) => setForm({ ...form, description: e.target.value })} />
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">URL pour postuler</Label>
              <Input data-testid="funding-form-url" value={form.url || ""} onChange={(e) => setForm({ ...form, url: e.target.value })} placeholder="https://..." />
            </div>
            <div>
              <Label className="text-xs">URL image (couverture)</Label>
              <Input data-testid="funding-form-image" value={form.image_url || ""} onChange={(e) => setForm({ ...form, image_url: e.target.value })} />
            </div>
          </div>
          <div className="grid sm:grid-cols-3 gap-3">
            <div>
              <Label className="text-xs">Date limite (deadline)</Label>
              <Input type="date" data-testid="funding-form-deadline" value={form.deadline || ""} onChange={(e) => setForm({ ...form, deadline: e.target.value })} />
            </div>
            <div>
              <Label className="text-xs">Montant (texte libre)</Label>
              <Input data-testid="funding-form-amount" value={form.amount_text || ""} onChange={(e) => setForm({ ...form, amount_text: e.target.value })} placeholder="500 000 XPF" />
            </div>
            <div>
              <Label className="text-xs">Bénéficiaires</Label>
              <Select value={form.target} onValueChange={(v) => setForm({ ...form, target: v })}>
                <SelectTrigger data-testid="funding-form-target"><SelectValue /></SelectTrigger>
                <SelectContent>{TARGETS.map((t) => <SelectItem key={t.v} value={t.v}>{t.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Catégorie principale</Label>
              <Select value={form.categories[0] || "subvention"} onValueChange={(v) => setForm({ ...form, categories: [v] })}>
                <SelectTrigger data-testid="funding-form-category"><SelectValue /></SelectTrigger>
                <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c.v} value={c.v}>{c.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Statut de publication</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger data-testid="funding-form-status"><SelectValue /></SelectTrigger>
                <SelectContent>{STATUSES.map((s) => <SelectItem key={s.v} value={s.v}>{s.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label className="text-xs">Disciplines concernées (séparées par virgule, vide = toutes)</Label>
            <Input data-testid="funding-form-disciplines" value={(form.disciplines || []).join(", ")} onChange={(e) => setForm({ ...form, disciplines: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })} placeholder="surf, va'a, karate" />
          </div>
          <div>
            <Label className="text-xs">Conditions d'éligibilité</Label>
            <Textarea rows={3} data-testid="funding-form-conditions" value={form.conditions || ""} onChange={(e) => setForm({ ...form, conditions: e.target.value })} placeholder="• Polynésien·ne résident·e ; • Licencié·e en discipline X ; • Dossier complet…" />
          </div>
          <div className="grid sm:grid-cols-3 gap-3">
            <div>
              <Label className="text-xs">Email contact</Label>
              <Input data-testid="funding-form-email" type="email" value={form.contact_email || ""} onChange={(e) => setForm({ ...form, contact_email: e.target.value })} />
            </div>
            <div>
              <Label className="text-xs">Téléphone</Label>
              <Input data-testid="funding-form-phone" value={form.contact_phone || ""} onChange={(e) => setForm({ ...form, contact_phone: e.target.value })} />
            </div>
            <div>
              <Label className="text-xs">Dossier PDF (URL)</Label>
              <Input data-testid="funding-form-docs" value={form.documents_url || ""} onChange={(e) => setForm({ ...form, documents_url: e.target.value })} />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Annuler</Button>
          <Button onClick={save} disabled={saving} data-testid="funding-form-save" className="bg-[hsl(var(--aito-lagoon))] text-black hover:bg-[hsl(var(--aito-lagoon-2))]">
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Save className="h-4 w-4 mr-1.5" />}
            {item ? "Enregistrer" : "Créer"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function AidesSportifsPage() {
  const { user } = useAuth();
  const isAdmin = !!user && (user.roles || []).some((r) => ["admin", "copf"].includes(r));

  const [items, setItems] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [target, setTarget] = useState(ALL);
  const [category, setCategory] = useState(ALL);
  const [includeClosed, setIncludeClosed] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  const load = () => {
    setLoading(true);
    const params = { limit: 100 };
    if (target !== ALL) params.target = target;
    if (category !== ALL) params.category = category;
    if (!includeClosed) params.status = "active";
    else delete params.status;
    api.get("/funding", { params })
      .then((r) => setItems(r.data.items || []))
      .catch((e) => { setItems([]); showError(e, { prefix: "Aides :" }); })
      .finally(() => setLoading(false));
  };
  useEffect(load, [target, category, includeClosed]);

  const filtered = useMemo(() => {
    if (!items) return [];
    const q = search.trim().toLowerCase();
    if (!q) return items;
    return items.filter((i) =>
      (i.title || "").toLowerCase().includes(q)
      || (i.organizer || "").toLowerCase().includes(q)
      || (i.description || "").toLowerCase().includes(q),
    );
  }, [items, search]);

  const handleDelete = async (item) => {
    const confirm = window.prompt(`Pour supprimer définitivement « ${item.title} », tapez SUPPRIMER`);
    if (confirm !== "SUPPRIMER") return;
    try {
      await api.delete(`/admin/entities/funding_opportunity/${item.id}?confirm=SUPPRIMER&reason=Admin+UI`);
      toast.success("Aide supprimée.");
      load();
    } catch (e) { showError(e, { prefix: "Suppression :" }); }
  };

  const hasFilters = search || target !== ALL || category !== ALL || includeClosed;
  const clearAll = () => { setSearch(""); setTarget(ALL); setCategory(ALL); setIncludeClosed(false); };

  return (
    <div className="mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8 py-8" data-testid="aides-sportifs-page">
      <header className="flex items-start justify-between gap-3 flex-wrap mb-6">
        <div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold flex items-center gap-2">
            <HandCoins className="h-7 w-7 text-[hsl(var(--aito-gold))]" />
            Aides aux sportifs
          </h1>
          <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
            Subventions, bourses, sponsorings et appels à projet ouverts aux athlètes, clubs, coachs et fédérations de Polynésie française.
          </p>
        </div>
        {isAdmin && (
          <Button onClick={() => { setEditingItem(null); setDialogOpen(true); }} data-testid="funding-new-btn" className="bg-[hsl(var(--aito-gold))] text-black hover:bg-[hsl(var(--aito-gold)/0.85)]">
            <Plus className="h-4 w-4 mr-1.5" />Nouvelle aide
          </Button>
        )}
      </header>

      <Card className="p-4 mb-6 space-y-3" data-testid="funding-filters">
        <div className="flex items-center gap-2">
          <Filter className="h-4 w-4 text-[hsl(var(--aito-lagoon))]" />
          <span className="text-sm font-medium">Filtres</span>
          {hasFilters && (
            <Button variant="ghost" size="sm" onClick={clearAll} className="ml-auto text-xs h-7">
              <X className="h-3 w-3 mr-1" />Réinitialiser
            </Button>
          )}
        </div>
        <div className="relative">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            data-testid="funding-search"
            placeholder="Rechercher (titre, organisme, projet)…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          <Select value={target} onValueChange={setTarget}>
            <SelectTrigger data-testid="funding-filter-target"><SelectValue placeholder="Bénéficiaires" /></SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>Tous bénéficiaires</SelectItem>
              {TARGETS.filter((t) => t.v !== "all").map((t) => <SelectItem key={t.v} value={t.v}>{t.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={category} onValueChange={setCategory}>
            <SelectTrigger data-testid="funding-filter-category"><SelectValue placeholder="Catégorie" /></SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>Toutes catégories</SelectItem>
              {CATEGORIES.map((c) => <SelectItem key={c.v} value={c.v}>{c.label}</SelectItem>)}
            </SelectContent>
          </Select>
          {isAdmin && (
            <label className="flex items-center gap-2 text-xs cursor-pointer self-center">
              <input type="checkbox" checked={includeClosed} onChange={(e) => setIncludeClosed(e.target.checked)} data-testid="funding-include-closed" />
              <span>Inclure les brouillons / clôturés</span>
            </label>
          )}
        </div>
      </Card>

      {loading ? (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="funding-loading">
          {[1, 2, 3, 4, 5, 6].map((i) => <Skeleton key={i} className="h-64 w-full" />)}
        </div>
      ) : filtered.length === 0 ? (
        <EmptyState
          icon={HandCoins}
          title={hasFilters ? "Aucune aide ne correspond" : "Aucune aide publiée pour le moment."}
          description={hasFilters
            ? "Modifie ou réinitialise les filtres pour élargir la recherche."
            : (isAdmin ? "Crée la première aide via le bouton « Nouvelle aide »." : "Reviens prochainement — les premières aides arrivent !")
          }
        />
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="funding-grid">
          {filtered.map((it) => (
            <FundingCard
              key={it.id}
              item={it}
              isAdmin={isAdmin}
              onEdit={(i) => { setEditingItem(i); setDialogOpen(true); }}
              onDelete={handleDelete}
            />
          ))}
        </div>
      )}

      <FundingFormDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        item={editingItem}
        onSaved={load}
      />
    </div>
  );
}
